 //External libs
 import { Injectable } from '@angular/core';
 import { HttpClient, HttpErrorResponse } from '@angular/common/http';
 import { Subscription } from 'rxjs';

 //Local libs
 import Service from 'src/app/common/service';
 import CultureEstimatorModel from './cultureEstimator.model';

/**
 * @service CultureEstimatorService
 * @description Service for managing culture estimation calculations and data retrieval.
 * Provides HTTP methods to fetch current and estimated culture metrics (biomass, FCR, survival, etc.)
 * for specific farm sections. Integrates with a Python-based backend service for complex calculations.
 * Extends base Service class with generic type CultureEstimatorModel.
 *
 * @feature 1. Fetch current and estimated culture data by section
 * @feature 2. Integration with specialized Python calculation engine
 * @feature 3. Cancel ongoing HTTP requests to prevent memory leaks
 * @feature 4. Promise-based async operations for estimation data
 *
 * @architecture
 * Extends base Service<CultureEstimatorModel>. Unlike standard services that use the default
 * Node.js backend, this service targets a specific Python microservice endpoint (`newPythonUrl`)
 * for estimation logic. Implements request tracking for cleanup.
 *
 * @service-endpoint /cultureCalculatorbysection
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
 @Injectable({
   providedIn: 'root'
 })
 export class CultureEstimatorService extends Service<CultureEstimatorModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
   public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient
   *
   * Initializes service by calling parent Service constructor with HttpClient
   * and the specific service endpoint path.
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests
   * @returns {void}
   */
   constructor(public httpClient: HttpClient) {
     super(httpClient, '/cultureCalculatorbysection');
   }

  /**
   * @method getCurrentAndEstimatedDataBySection
   * @async
   * @description Fetch current status and future estimates for a culture section
   *
   * Makes HTTP POST request to the Python backend service. Sends parameters like
   * farm site ID, section ID, and user details to calculate culture metrics.
   *
   * @param {Object} cultureEstimatorReqBody - Request payload containing:
   *   - farm_site_id: ID of the farm site
   *   - section_id: ID of the section
   *   - farmNameAtLocation: Name of farm
   *   - personName: User requesting report
   *   - mobileNumber: User contact
   *   - businessName: Business context
   *
   * @returns {Promise<CultureEstimatorModel>} Promise resolving to CultureEstimatorModel containing current and estimated data
   * @side-effect
   * - Creates HTTP POST request to Python backend
   * - Stores subscription reference in ongoingRequests
   *
   * @throws Rejects promise with error message string if request fails
   * @example
   * const reqBody = {
   *   farm_site_id: '123',
   *   section_id: '456',
   *   ...
   * };
   * const estimates = await service.getCurrentAndEstimatedDataBySection(reqBody);
   */
   public getCurrentAndEstimatedDataBySection(cultureEstimatorReqBody: Object) {
     return new Promise<CultureEstimatorModel>((resolve, reject) => {
       const request = this.httpClient.post<CultureEstimatorModel>(`${this.newPythonUrl}${this.serviceUrl}`, cultureEstimatorReqBody, this.httpOptions).subscribe(response => {
         resolve(response);
       }, (error: HttpErrorResponse) => {
         reject(error.error.message);
       });
       this.ongoingRequests.push(request);
     });
   }

    //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks
   *
   * Iterates through ongoingRequests array and unsubscribes from each active
   * subscription. Clears the requests array after unsubscribing from all.
   *
   * @execution-flow
   * 1. Iterate through each subscription in ongoingRequests array
   * 2. Check if subscription has unsubscribe function
   * 3. Call unsubscribe() on each active subscription
   * 4. Clear ongoingRequests array
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests
   * - Clears ongoingRequests array
   *
   * @usage Called during component ngOnDestroy to cleanup subscriptions
   * @example
   * ngOnDestroy() {
   *   this.cultureEstimatorService.cancelOnGoingHttpRequests();
   * }
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
 }
